import { useRef, useState } from "react";
import { Image as ImageIcon, Link2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { fileToDataUrl } from "../../lib/storage";

export default function ProductImagePicker({ image, initialMode = "url", onChange }) {
  const [mode, setMode] = useState(initialMode);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setUploading(true);
    try {
      // Product images are shown as small catalog thumbnails. Keeping uploads
      // compact avoids exhausting localStorage on iPad/PWA installs after a
      // catalog accumulates many photographed products.
      onChange(await fileToDataUrl(file, 420, 0.68));
    } catch (_e) {
      toast.error("Couldn't read that image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Reference picture</Label>
      <div className="mt-2 flex gap-3">
        <div className="h-24 w-24 rounded-lg border border-[#E6E0D4] bg-white overflow-hidden flex items-center justify-center shrink-0 p-2">
          {image ? (
            <img
              src={image}
              alt=""
              className="h-full w-full object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <ImageIcon size={24} className="text-[#0B1F3A]/25" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="bg-white border border-[#E6E0D4] h-9">
              <TabsTrigger value="url" className="text-xs gap-1.5"><Link2 size={12} /> URL</TabsTrigger>
              <TabsTrigger value="upload" className="text-xs gap-1.5"><Upload size={12} /> Upload</TabsTrigger>
            </TabsList>
            <TabsContent value="url" className="mt-2">
              <Input
                value={image?.startsWith("data:") ? "" : image}
                onChange={(e) => onChange(e.target.value)}
                placeholder="https://…"
                className="h-9 bg-white border-[#E6E0D4]"
              />
            </TabsContent>
            <TabsContent value="upload" className="mt-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="border-[#E6E0D4] bg-white"
                >
                  <Upload size={14} className="mr-1.5" />
                  {uploading ? "Reading…" : "Choose file"}
                </Button>
                {image && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")}>
                    <X size={14} className="mr-1" /> Remove
                  </Button>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
